import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiPatch } from '../../lib/api';

export default function Register() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, signIn } = useAuth();
  const roleParam = searchParams.get('role');
  const defaultRole = roleParam === 'customer' ? 'customer' : 'merchant_owner';
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: searchParams.get('phone') || '',
    password: '',
    role: defaultRole,
  });
  const [busy, setBusy] = useState(false);
  const isCustomer = form.role === 'customer';
  const heading = isCustomer ? 'Create your customer account' : 'Create your RozOps account';
  const subtitle = isCustomer
    ? 'Connect with your service providers and manage subscriptions in one place.'
    : 'Free forever for up to 25 customers';

  const submit = async (e) => {
    e.preventDefault();
    const phone = form.phone.replace(/\D/g, '');
    if (phone.length < 10) {
      toast.error('Enter a valid mobile number');
      return;
    }
    setBusy(true);
    try {
      await signUp(form.email, form.password, { full_name: form.full_name, phone, role: form.role });
      // Sign in immediately (assuming email confirmation disabled or auto)
      try {
        await signIn(form.email, form.password);
        await apiPatch('/auth/profile', { full_name: form.full_name, phone, role: form.role });
      } catch { /* if email confirmation required */ }
      toast.success('Account created. Complete your setup.');
      nav('/onboarding');
    } catch (err) {
      toast.error(err.message || 'Sign up failed');
    } finally {
      setBusy(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue transition-all';

  return (
    <div className="min-h-screen bg-rozops-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8">
          <img src="/ro-hlogo.png" alt="RozOps" className="h-16 w-auto object-contain" />
        </Link>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
          <h1 className="font-heading font-bold text-2xl text-rozops-navy">{heading}</h1>
          <p className="text-sm text-rozops-text-muted mt-1">{subtitle}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-rozops-text mb-1.5">Your name</label>
              <input required data-testid="reg-name" className={inputCls} value={form.full_name} onChange={upd('full_name')} placeholder="Ramesh Kumar" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-rozops-text mb-1.5">Email</label>
              <input required type="email" data-testid="reg-email" className={inputCls} value={form.email} onChange={upd('email')} placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-rozops-text mb-1.5">Mobile number</label>
              <input required type="tel" inputMode="numeric" data-testid="reg-phone" className={inputCls} value={form.phone} onChange={upd('phone')} minLength={10} placeholder="9876543210" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-rozops-text mb-1.5">Password</label>
              <input required type="password" data-testid="reg-password" className={inputCls} value={form.password} onChange={upd('password')} minLength={6} placeholder="At least 6 characters" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-rozops-text mb-1.5">I am a…</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm({ ...form, role: 'merchant_owner' })} data-testid="reg-role-merchant" className={`rounded-xl px-4 py-3 border-2 font-semibold text-sm transition-all ${form.role === 'merchant_owner' ? 'border-rozops-accent bg-rozops-accent-light text-rozops-accent' : 'border-gray-200 text-rozops-text'}`}>Business owner</button>
                <button type="button" onClick={() => setForm({ ...form, role: 'customer' })} data-testid="reg-role-customer" className={`rounded-xl px-4 py-3 border-2 font-semibold text-sm transition-all ${form.role === 'customer' ? 'border-rozops-accent bg-rozops-accent-light text-rozops-accent' : 'border-gray-200 text-rozops-text'}`}>Customer</button>
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="reg-submit" className="w-full bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-4 py-3 font-semibold shadow-soft hover:shadow-medium transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create account
            </button>
          </form>
          <div className="mt-6 text-sm text-center text-rozops-text-muted">
            Have an account? <Link to="/login" className="text-rozops-blue font-semibold">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
